import { ref } from 'vue';
import { defineStore } from 'pinia';
import { message } from 'ant-design-vue';
import { get, post, destroy } from '@/api';
import {
  fetchOrgNotifications,
  fetchOrgNotificationUnreadCount,
  markOrgNotificationRead,
  markAllOrgNotificationsRead,
  deleteOrgNotification,
  clearAllOrgNotifications,
} from '@/api/organisation';
import { useAuthOrganisationStore } from '@/stores/authOrganisation';
import { storageKeys } from '@/constants';

const POLL_INTERVAL_MS = 60_000;
const ORG_ACCESS_TOKEN_KEY = 'org_access_token';

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref([]);
  const unreadCount = ref(0);
  const isLoading = ref(false);

  // Selected once per host layout via `configure({ mode })`.
  let mode = 'student';
  let pollTimer = null;
  let pollInFlight = false;
  let pollingEnabled = false;
  let authRejected = false;
  let listenersAttached = false;
  let lastUnread = 0;
  let initialised = false;

  function configure(options = {}) {
    if (options.mode) mode = options.mode;
  }

  function credentialKey() {
    return mode === 'org' ? ORG_ACCESS_TOKEN_KEY : storageKeys.accessToken;
  }

  function hasCredential() {
    return Boolean(localStorage.getItem(credentialKey()));
  }

  function pageIsVisible() {
    return (
      typeof document === 'undefined' || document.visibilityState !== 'hidden'
    );
  }

  function clearPollTimer() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  // Resolve the correct transport (token + endpoints) for the active context.
  // Student calls go through the default `@/api` axios (accessToken); org-admin
  // calls go through the org axios with the org_access_token.
  function client() {
    if (mode === 'org') {
      const token = useAuthOrganisationStore().accessToken;
      return {
        list: () => fetchOrgNotifications(token),
        unread: () => fetchOrgNotificationUnreadCount(token),
        read: (uuid) => markOrgNotificationRead(token, uuid),
        readAll: () => markAllOrgNotificationsRead(token),
        remove: (uuid) => deleteOrgNotification(token, uuid),
        clearAll: () => clearAllOrgNotifications(token),
      };
    }
    return {
      list: () => get('api/notifications/'),
      unread: () => get('api/notifications/unread-count/'),
      read: (uuid) => post(`api/notifications/${uuid}/read/`),
      readAll: () => post('api/notifications/mark-all-read/'),
      remove: (uuid) => destroy(`api/notifications/${uuid}/`),
      clearAll: () => destroy('api/notifications/clear-all/'),
    };
  }

  async function fetchNotifications() {
    isLoading.value = true;
    try {
      const res = await client().list();
      if (res?.status === 200) {
        items.value = res.data?.results ?? [];
      }
      return res;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchUnreadCount() {
    // A dashboard tab can remain mounted after another tab logs out and clears
    // localStorage. Never send an unauthenticated polling request from it.
    if (!hasCredential()) {
      clearPollTimer();
      return null;
    }

    const res = await client().unread();
    if (res?.status === 401) {
      // Invalid/expired sessions must not generate a permanent 401 loop. A new
      // token (login/refresh in another tab) clears this pause via `storage`.
      authRejected = true;
      clearPollTimer();
      return res;
    }
    if (res?.status === 200) {
      authRejected = false;
      const next = res.data?.unread ?? 0;
      // Toast only on genuine increases after the first poll (avoids a toast on
      // every page load when there are pre-existing unread notifications).
      if (initialised && next > lastUnread) {
        await fetchNotifications();
        const newest = items.value.find((n) => !n.is_read);
        if (newest) message.info(newest.title);
      }
      lastUnread = next;
      unreadCount.value = next;
      initialised = true;
    }
    return res;
  }

  async function poll() {
    clearPollTimer();
    if (
      pollInFlight ||
      !pollingEnabled ||
      authRejected ||
      !hasCredential() ||
      !pageIsVisible()
    ) {
      return;
    }

    pollInFlight = true;
    try {
      await fetchUnreadCount();
    } finally {
      pollInFlight = false;
    }
    if (pollingEnabled && !authRejected && hasCredential() && pageIsVisible()) {
      pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    }
  }

  function onVisibilityChange() {
    if (!pageIsVisible()) {
      clearPollTimer();
      return;
    }
    if (pollingEnabled && !authRejected && hasCredential()) void poll();
  }

  function onStorage(event) {
    // `localStorage.clear()` (used by student logout) emits a storage event
    // with a null key, while org logout removes its token by name.
    if (event.key !== null && event.key !== credentialKey()) return;
    clearPollTimer();
    authRejected = false;
    if (pollingEnabled && event.newValue && pageIsVisible()) void poll();
  }

  function attachLifecycleListeners() {
    if (listenersAttached || typeof window === 'undefined') return;
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibilityChange);
    listenersAttached = true;
  }

  function detachLifecycleListeners() {
    if (!listenersAttached || typeof window === 'undefined') return;
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listenersAttached = false;
  }

  async function markRead(uuid) {
    const res = await client().read(uuid);
    if (res?.status === 200) {
      const item = items.value.find((n) => n.uuid === uuid);
      if (item) {
        item.is_read = true;
        item.read_at = new Date().toISOString();
      }
      unreadCount.value =
        res.data?.unread ?? Math.max(0, unreadCount.value - 1);
      lastUnread = unreadCount.value;
    }
    return res;
  }

  async function markAllRead() {
    const res = await client().readAll();
    if (res?.status === 200) {
      items.value.forEach((n) => {
        n.is_read = true;
      });
      unreadCount.value = 0;
      lastUnread = 0;
    }
    return res;
  }

  async function deleteNotification(uuid) {
    const res = await client().remove(uuid);
    if (res?.status === 200) {
      items.value = items.value.filter((n) => n.uuid !== uuid);
      unreadCount.value = res.data?.unread ?? unreadCount.value;
      lastUnread = unreadCount.value;
    }
    return res;
  }

  async function clearAll() {
    const res = await client().clearAll();
    if (res?.status === 200) {
      items.value = [];
      unreadCount.value = 0;
      lastUnread = 0;
    }
    return res;
  }

  function startPolling() {
    clearPollTimer();
    pollingEnabled = true;
    authRejected = false;
    attachLifecycleListeners();
    if (hasCredential() && pageIsVisible()) void poll();
  }

  function stopPolling() {
    pollingEnabled = false;
    authRejected = false;
    clearPollTimer();
    detachLifecycleListeners();
  }

  function reset() {
    stopPolling();
    items.value = [];
    unreadCount.value = 0;
    lastUnread = 0;
    initialised = false;
  }

  return {
    items,
    unreadCount,
    isLoading,
    configure,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
    deleteNotification,
    clearAll,
    startPolling,
    stopPolling,
    reset,
  };
});
